// This file is for rendering filters in chart filter section.
// it is called from DropZone.tsx

import { useState, useEffect, useMemo } from 'react';
import '../ChartAxes/Card.css';
import './UserFilterCard.css';
import { useDrag, useDrop } from 'react-dnd';

import {
  revertAxes,
  sortAxes,
  updateAxesQueryParam,
  updtateFilterExpandeCollapse,
} from '../../redux/ChartPoperties/ChartPropertiesActions';
import LoadingPopover from '../CommonFunctions/PopOverComponents/LoadingPopover';
import moment from 'moment';

import { UserFilterCardProps } from './UserFilterCardInterface';
import { AxisType } from '../CommonFunctions/aliases';
import {
  datePatternCollections,
  datePatternSearchConditionCollections,
} from '../FilterUtils/FilterPatternCollection';
import { useGetActions } from '../FilterUtils/FetchFilterData';
import { SelecRelativeFilterCard } from '../FilterUtils/RelativeFilter';
import { CustomCard } from '../FilterUtils/SearchCondition';
import { SelecPickListCard } from '../FilterUtils/PickList';
import { SelecTillDate } from '../FilterUtils/TillDate';
import { DropDownForDatePattern } from '../FilterUtils/DropDownComponent';
import { FilterField } from '../FilterUtils/FilterField';
import { useSelector } from 'react-redux';
import { RootState } from '../../redux';
import { useDispatch } from 'react-redux';
import SelectParameter from './SelectParameter';
import { callQueryAPI, SetModifyOptions } from '../../redux/ChartPoperties/ChartControlsActions';
import TopN from '../FilterUtils/TopN';
import AggregateCondition from '../FilterUtils/AggregateCondition';

const UserFilterCard = ({ propKey, field, bIndex, itemIndex, uID }: UserFilterCardProps) => {
  field.dataType = field.dataType?.toLowerCase();

  const { chartProp, token, calculations, parameters } = useSelector((state: RootState) => ({
    chartProp: state.chartProperties,
    token: state.isLogged.accessToken,
    calculations: state.calculations,
    parameters: state.parameters,
  }));

  const dispatch = useDispatch();

  let currentChartAxesName = uID ? 'chartAxes_' + uID : 'chartAxes';
  let currentChartAxes = JSON.parse(
    JSON.stringify(chartProp.properties[propKey][currentChartAxesName]),
  );

  const { uId, fieldname, displayname, dataType, tableId } = field;
  var isCollapsed: boolean = currentChartAxes[0].isCollapsed;

  useEffect(() => {
    var res = currentChartAxes[0].fields.map((el: any) => {
      el.isCollapsed = !currentChartAxes[0].isCollapsed;
      return el;
    });

    dispatch(updtateFilterExpandeCollapse(propKey, bIndex, res, currentChartAxesName));
  }, [isCollapsed]);

  const originalIndex = currentChartAxes[bIndex].fields.findIndex((item: any) => item.uId === uId);

  const [loading, setLoading] = useState(false);
  const [cirloading, setCirLoading] = useState<boolean>(false);
  let sliderRange = [0, 0];

  let filterFieldData = JSON.parse(JSON.stringify(field));

  const { GetPickListItems, GetRelativeFilterItems } = useGetActions({
    sortOptions: [],
    filterFieldData,
    field,
    propKey,
    token,
    uID,
    itemIndex,
  });

  const callAPI = () => {
    if (!uID) dispatch(callQueryAPI(propKey, true)); // only call query API if not an override field
  };

  const parameterSourceValues = useMemo(() => {
    if (!field.SavedCalculationUUID) return [];
    const calculation = calculations.savedCalculations.find(
      (calculation) => calculation.uuid === field.SavedCalculationUUID,
    );
    if (!calculation) return [];
    const calculationInfo = calculation.calculationInfo;
    const activeConditionIndex = calculation.activeCondition;
    const activeFlow = calculation.activeFlow;
    if (
      !calculationInfo ||
      !Number.isInteger(activeConditionIndex) ||
      activeConditionIndex === null
    ) {
      return [];
    }
    const flow = calculationInfo.flows?.[activeFlow!]?.[activeConditionIndex];
    if (!flow || !Array.isArray(flow.parameter) || !Array.isArray(flow.source)) {
      return [];
    }
    return flow.parameter.map((p: any) => flow.source?.[p.index]);
  }, [field.SavedCalculationUUID, calculations.savedCalculations]);

  /* Initialize variable to default values */

  useEffect(() => {
    if (!filterFieldData.includeexclude) filterFieldData['includeexclude'] = 'Include';
    if (!filterFieldData.exprInput) filterFieldData['exprInput'] = '';

    if (filterFieldData && filterFieldData.dataType && !filterFieldData.isAggregated) {
      switch (filterFieldData.dataType) {
        case 'decimal':
        case 'integer':
          if (!filterFieldData.fieldtypeoption) {
            filterFieldData['fieldtypeoption'] = 'Search Condition';
          }
          break;
        case 'date':
        case 'timestamp':
          if (!filterFieldData.fieldtypeoption) {
            filterFieldData['prefix'] = 'year';
            filterFieldData['fieldtypeoption'] = 'Pick List';
          }
          break;
        default:
          if (!filterFieldData.fieldtypeoption) {
            filterFieldData['fieldtypeoption'] = 'Pick List';
          }
          break;
      }
    }

    if (filterFieldData.fieldtypeoption === 'Search Condition') {
      initialSearchConditionValues();
    } else if (filterFieldData.fieldtypeoption === 'Pick List') {
      if (
        filterFieldData &&
        filterFieldData.dataType &&
        filterFieldData.dataType === 'timestamp' &&
        !filterFieldData.prefix
      ) {
        filterFieldData['prefix'] = 'year';
      }

      filterFieldData['exprTypeTillDate'] = filterFieldData['exprTypeTillDate'] ?? false;
      filterFieldData['filterTypeTillDate'] = 'enabled';

      if (!filterFieldData.isParameterized) {
        async function _preFetchData() {
          // if (!filterFieldData.rawselectmembers || filterFieldData.rawselectmembers.length === 0) {
          setLoading(true);
          await GetPickListItems();
          setLoading(false);
          // }
        }

        _preFetchData();
      }
    } else if (filterFieldData.fieldtypeoption === 'Relative Filter') {
      initialRelativeFilterData();
    }
    if (filterFieldData.isAggregated && !filterFieldData.fieldtypeoption) {
      filterFieldData.fieldtypeoption = 'Aggregate Condition';
      filterFieldData.aggr = 'sum';
      filterFieldData.exprType = 'greaterThan';
    }
    updateUserFilterItem(
      propKey,
      0,
      itemIndex,
      constructChartAxesFieldObject(),
      currentChartAxesName,
    );
    // eslint-disable-next-line
  }, [parameterSourceValues, filterFieldData.isParameterized]);

  const updateUserFilterItem = (
    propKey: string,
    bIndex: number,
    itemIndex: number,
    field: any,
    axesName: string,
  ) => {
    dispatch(updateAxesQueryParam(propKey, bIndex, itemIndex, field, axesName));
  };

  const isOverride = Boolean(uID);
  /// Properties and behaviour when a filter card is dragged
  const [, drag] = useDrag({
    item: {
      uId: uId,
      fieldname: fieldname,
      displayname: displayname,
      dataType: dataType,
      tableId: tableId,
      // type: "card",
      bIndex,
      originalIndex,
      dragFrom: isOverride ? `${AxisType.ChartFilter}-override` : AxisType.ChartFilter,
      SavedCalculationUUID: field.SavedCalculationUUID,
      overrideUId: uID
    },
    type: 'card',

    end: (dropResult, monitor) => {
      const { uId, bIndex, originalIndex } = monitor.getItem();

      const didDrop = monitor.didDrop();

      if (uId && !didDrop) {
        dispatch(revertAxes(propKey, bIndex, uId, originalIndex, currentChartAxesName));
      }
    },
  });

  // Properties and behaviours when another filter card is dropped over this card
  const [, drop] = useDrop({
    accept: ['card', 'calculation'],
    canDrop: () => false,
    collect: (monitor) => ({
      backgroundColor1: monitor.isOver({ shallow: true }) ? 1 : 0,
    }),
    hover(item: any) {
      if (!item || !item.uId || !item.bIndex || !item.dragFrom) return;

      const dragUId = item?.uId;
      const fromBIndex = item?.bIndex;
      const dragFromOverride = item?.dragFrom?.includes("override");
      const overrideDropZone = Boolean(uID);

      // invalid movements
      if (dragFromOverride && !overrideDropZone) return;
      if (!dragFromOverride && overrideDropZone && item.bIndex !== 99) return;
      if (overrideDropZone && dragFromOverride && item?.overrideUId !== uID) return;

      if (fromBIndex === bIndex && uId && dragUId !== uId) {
        dispatch(sortAxes(propKey, bIndex, dragUId, uId, currentChartAxesName));
      }
    },
  });

  ///Initialize Relative Filter items
  const initialRelativeFilterData = () => {
    if (!filterFieldData.expTypeFromdate) {
      filterFieldData['expTypeFromdate'] = 'year';
    }
    if (!filterFieldData.expTypeTodate) {
      filterFieldData['expTypeTodate'] = 'day';
    }
    if (!filterFieldData.expTypeFromRelativeDate) {
      filterFieldData['expTypeFromRelativeDate'] = 'last';
    }
    if (!filterFieldData.expTypeToRelativeDate) {
      filterFieldData['expTypeToRelativeDate'] = 'last';
    }
    if (!filterFieldData.expTypeAnchorDate) {
      filterFieldData['expTypeAnchorDate'] = 'today';
    }
    if (!filterFieldData.exprInputFromValueType) {
      filterFieldData['exprInputFromValueType'] = 2;
    }
    if (!filterFieldData.exprInputToValueType) {
      filterFieldData['exprInputToValueType'] = 2;
    }
    if (!filterFieldData.exprInputSpecificDate) {
      filterFieldData['exprInputSpecificDate'] = moment(new Date()).format('YYYY-MM-DD');
    }
  };

  ///Initial Search Condition Values
  const initialSearchConditionValues = () => {
    if (filterFieldData.fieldtypeoption === 'Search Condition') {
      if (filterFieldData.dataType) {
        switch (filterFieldData.dataType) {
          case 'decimal':
          case 'integer':
            if (!filterFieldData.exprType) {
              filterFieldData['exprType'] = 'greaterThan';
            }
            break;
          case 'text':
            if (!filterFieldData.exprType) {
              filterFieldData['exprType'] = 'beginsWith';
            }
            break;
          case 'timestamp':
          case 'date':
            if (!filterFieldData.exprType) {
              filterFieldData['prefix'] = 'year';
              filterFieldData['exprType'] = 'greaterThan';
            }
            break;
          default:
            if (!filterFieldData.exprType) {
              filterFieldData['exprType'] = 'greaterThan';
            }
            break;
        }
      }
    }
  };

  ///Construct Chart property object
  const constructChartAxesFieldObject = () => {
    return filterFieldData;
  };

  ///set Search condition condition initiallize slider control
  const setSliderRange = () => {
    if (
      ['float', 'decimal', 'double', 'integer'].includes(field.dataType) &&
      filterFieldData.exprType === 'between'
    ) {
      if (
        filterFieldData.rawselectmembersOriginal &&
        !filterFieldData.greaterThanOrEqualTo &&
        !filterFieldData.lessThanOrEqualTo
      ) {
        filterFieldData.greaterThanOrEqualTo = filterFieldData.rawselectmembersOriginal[1];
        if (typeof filterFieldData.rawselectmembersOriginal[1] === 'string') {
          filterFieldData.greaterThanOrEqualTo = null;
        }
        filterFieldData.lessThanOrEqualTo =
          filterFieldData.rawselectmembersOriginal[
            filterFieldData.rawselectmembersOriginal.length - 1
          ];
        if (
          filterFieldData.rawselectmembersOriginal[
            filterFieldData.rawselectmembersOriginal.length - 1
          ] === '(Blank)'
        ) {
          filterFieldData.lessThanOrEqualTo =
            filterFieldData.rawselectmembersOriginal[
              filterFieldData.rawselectmembersOriginal.length - 2
            ];
          if (
            typeof filterFieldData.rawselectmembersOriginal[
              filterFieldData.rawselectmembersOriginal.length - 2
            ] === 'string'
          ) {
            filterFieldData.lessThanOrEqualTo = null;
          }
        }
      }
    } else if (
      ['date', 'timestamp'].includes(field.dataType) &&
      filterFieldData.prefix !== 'date'
    ) {
      if (filterFieldData.prefix === 'month') {
        filterFieldData.greaterThanOrEqualTo = 1;
        filterFieldData.lessThanOrEqualTo = 12;
      } else if (filterFieldData.prefix === 'quarter') {
        filterFieldData.greaterThanOrEqualTo = 1;
        filterFieldData.lessThanOrEqualTo = 4;
      } else if (filterFieldData.prefix === 'dayofmonth') {
        filterFieldData.greaterThanOrEqualTo = 1;
        filterFieldData.lessThanOrEqualTo = 31;
      } else if (filterFieldData.prefix === 'dayofweek') {
        filterFieldData.greaterThanOrEqualTo = 1;
        filterFieldData.lessThanOrEqualTo = 7;
      } else {
        filterFieldData.greaterThanOrEqualTo = filterFieldData.rawselectmembersOriginal[1];
        if (typeof filterFieldData.rawselectmembersOriginal[1] === 'string') {
          filterFieldData.greaterThanOrEqualTo = null;
        }
        filterFieldData.lessThanOrEqualTo =
          filterFieldData.rawselectmembersOriginal[
            filterFieldData.rawselectmembersOriginal.length - 1
          ];
        if (
          filterFieldData.rawselectmembersOriginal[
            filterFieldData.rawselectmembersOriginal.length - 1
          ] === '(Blank)'
        ) {
          filterFieldData.lessThanOrEqualTo =
            filterFieldData.rawselectmembersOriginal[
              filterFieldData.rawselectmembersOriginal.length - 2
            ];
          if (
            typeof filterFieldData.rawselectmembersOriginal[
              filterFieldData.rawselectmembersOriginal.length - 2
            ] === 'string'
          ) {
            filterFieldData.lessThanOrEqualTo = null;
          }
        }
      }
    }
  };

  const checkValidDate = (val: any) => {
    if (
      ['date', 'timestamp'].includes(field.dataType) &&
      filterFieldData.prefix === 'date' &&
      val.includes('-')
    ) {
      return true;
    }
    if (
      ['date', 'timestamp'].includes(field.dataType) &&
      filterFieldData.fieldtypeoption === 'Relative Filter' &&
      val.includes('-')
    ) {
      return true;
    }

    return false;
  };

  const setDefaultDate = (key: string, value: any) => {
    if (filterFieldData[key]) {
      filterFieldData[key] = value ? value : new Date();
    }
  };

  const setSearchConditionDate = () => {
    if (['date', 'timestamp'].includes(field.dataType) && filterFieldData.prefix === 'date') {
      if (filterFieldData.exprType === 'between') {
        if (checkValidDate(filterFieldData.exprInput)) {
          setDefaultDate('greaterThanOrEqualTo', filterFieldData.exprInput);
          setDefaultDate('lessThanOrEqualTo', filterFieldData.exprInput);
        }
      } else {
        if (checkValidDate(filterFieldData.lessThanOrEqualTo)) {
          setDefaultDate('exprInput', filterFieldData.lessThanOrEqualTo);
        }
      }
    }
  };

  ///Search Condition and Relative Filter Dropdown list on change handler
  const handleDropDownForPatternOnChange = async (event: any, key = 'exprType') => {
    filterFieldData[key] = event.target.value;
    if (filterFieldData.fieldtypeoption === 'Relative Filter') {
      setCirLoading(true);
      await GetRelativeFilterItems();
      setCirLoading(false);
    } else {
      if (
        filterFieldData.exprType === 'between' &&
        'Search Condition' === filterFieldData.fieldtypeoption
      ) {
        //setLoading(true);
        await GetPickListItems();
        setSliderRange();
        // setLoading(false);
      }
    }
    setSearchConditionDate();

    if (['Search Condition', 'Aggregate Condition'].includes(filterFieldData.fieldtypeoption)) {
      if (filterFieldData.exprType === 'between') {
        filterFieldData.switchEnableSearchCondition = true;
      } else {
        if (!filterFieldData.exprInput || !filterFieldData.exprInput.length) {
          filterFieldData.switchEnableSearchCondition = false;
          filterFieldData.exprTypeTillDate = false;
        } else {
          filterFieldData.switchEnableSearchCondition = true;
        }
      }
    }
    dispatch(SetModifyOptions(propKey, 'filter', filterFieldData.uId));
    if (filterFieldData.fieldtypeoption !== 'Relative Filter') {
      updateUserFilterItem(
        propKey,
        0,
        itemIndex,
        constructChartAxesFieldObject(),
        currentChartAxesName,
      );
      callAPI();
    }
  };

  ///Handle Date time grain dropdown list change
  const handleDropDownForDatePatternOnChange = async (event: any) => {
    filterFieldData['prefix'] = event.target.value;
    filterFieldData['greaterThanOrEqualTo'] = '';
    filterFieldData['lessThanOrEqualTo'] = '';
    filterFieldData.filterTypeTillDate = 'enabled';
    if (filterFieldData.prefix === 'date') filterFieldData.exprTypeTillDate = false;
    if (!filterFieldData.exprInput) {
      filterFieldData['switchEnableSearchCondition'] = false;
      filterFieldData.exprTypeTillDate = false;
    }
    if (filterFieldData.fieldtypeoption === 'Search Condition' && event.target.value === 'date') {
      filterFieldData['exprInput'] = moment(new Date()).format('YYYY-MM-DD');
    }
    if (
      ['Search Condition', 'Aggregate Condition'].includes(filterFieldData.fieldtypeoption) &&
      event.target.value !== 'date'
    ) {
      if (filterFieldData['exprInput']?.includes('-')) {
        filterFieldData['exprInput'] = '';
        filterFieldData['switchEnableSearchCondition'] = false;
      }
      if (filterFieldData.exprType === 'between')
        filterFieldData['switchEnableSearchCondition'] = true;
    }
    if (filterFieldData.fieldtypeoption === 'Pick List') {
      if (filterFieldData.userSelection.length > 0) {
        filterFieldData['callAPI'] = true;
      } else {
        filterFieldData['callAPI'] = false;
      }
    }

    // No need for unwanted filter-API calls when parameterized
    if (filterFieldData.fieldtypeoption === 'Pick List' && !filterFieldData.isParameterized) {
      setLoading(true);
      await GetPickListItems();
      setLoading(false);
      setSliderRange();
    }
    setSearchConditionDate();

    callAPI();
    updateUserFilterItem(
      propKey,
      0,
      itemIndex,
      constructChartAxesFieldObject(),
      currentChartAxesName,
    );
    dispatch(SetModifyOptions(propKey, 'filter', filterFieldData.uId));
  };

  ///Till Date Switch On Click
  const handleChangeTillDate = () => {
    if (filterFieldData.exprTypeTillDate === false) {
      filterFieldData['exprTypeTillDate'] = true;
    } else {
      filterFieldData['exprTypeTillDate'] = false;
    }
    dispatch(SetModifyOptions(propKey, 'filter', `${filterFieldData.uId}_tilldate`));
    updateUserFilterItem(
      propKey,
      0,
      itemIndex,
      constructChartAxesFieldObject(),
      currentChartAxesName,
    );
    callAPI();
  };

  ///Pick list CB change
  const handleCBChange = (event: any) => {
    if (event.target.name === '(All)') {
      if (event.target.checked) {
        filterFieldData['userSelection'] = filterFieldData.rawselectmembers.map((item: any) => {
          if (item === '(Blank)') {
            return null;
          }
          if (item === '(Empty') return '';
          return item;
        });
        filterFieldData['filterTypeTillDate'] = 'enabled';
      } else {
        filterFieldData['userSelection'] = [];
      }
    } else {
      filterFieldData['filterTypeTillDate'] = 'disabled';

      if (event.target.checked) {
        if (!isNaN(event.target.name) && isFinite(event.target.name)) {
          let _name = event.target.name;
          if (_name.includes('.')) {
            _name = parseFloat(event.target.name);
          } else {
            _name = parseInt(event.target.name);
          }
          if (_name) filterFieldData.userSelection.push(_name);
        } else {
          if (event.target.name === '(Blank)') {
            filterFieldData.userSelection.push(null);
          } else if (event.target.name === '(Empty)') filterFieldData.userSelection.push('');
          else {
            filterFieldData.userSelection.push(event.target.name);
          }
        }
      } else {
        let idx = filterFieldData.userSelection.findIndex((item: any) => {
          if (item === null) {
            item = '(Blank)';
          } else if (item === '') item = '(Empty)';
          return item.toString() === event.target.name.toString();
        });

        if (idx >= 0) {
          filterFieldData.userSelection.splice(idx, 1);
        }
      }

      if (!filterFieldData.userSelection.length) {
        filterFieldData['filterTypeTillDate'] = 'enabled';
      }
      let AllIdx = filterFieldData.userSelection.findIndex((item: any) => {
        if (item === null) {
          item = '(Blank)';
        } else if (item === '') item = '(Empty)';
        return item.toString() === '(All)';
      });

      if (AllIdx >= 0) {
        filterFieldData.userSelection.splice(AllIdx, 1);
      }
    }
    filterFieldData.userSelection = filterFieldData.userSelection.map((item: any) => {
      if (item === '(Blank)') {
        return null;
      }
      if (item === '(Empty') return '';
      return item;
    });

    const nullIndex = filterFieldData.userSelection.indexOf(null);
    if (nullIndex >= 0) {
      filterFieldData.userSelection = [
        ...filterFieldData.userSelection.filter((item: any) => item !== null),
        null,
      ];
    }
    if (
      filterFieldData.userSelection.length ===
      filterFieldData.rawselectmembersOriginal?.length - 1
    ) {
      filterFieldData.userSelection = ['(All)', ...filterFieldData.userSelection];
    }
    dispatch(SetModifyOptions(propKey, 'filter', filterFieldData.uId));
    if (filterFieldData.userSelection?.length <= 0) filterFieldData['callAPI'] = true;
    else filterFieldData['callAPI'] = false;

    updateUserFilterItem(
      propKey,
      0,
      itemIndex,
      constructChartAxesFieldObject(),
      currentChartAxesName,
    );
    callAPI();
  };

  const checkForValidData = () => {
    if (filterFieldData.exprType === 'blank') return true;
    else if (
      filterFieldData.prefix === 'date' &&
      new Date(filterFieldData.greaterThanOrEqualTo) > new Date(filterFieldData.lessThanOrEqualTo)
    ) {
      filterFieldData['isInValidData'] = true;
    } else {
      if (
        parseInt(filterFieldData.greaterThanOrEqualTo) > parseInt(filterFieldData.lessThanOrEqualTo)
      ) {
        filterFieldData['isInValidData'] = true;
      }
    }
  };

  ///Search Condition and Relative Filter user input change handler
  const handleCustomRequiredValueOnBlur = async (
    val: number | string | Date,
    key = 'exprInput',
    type?: string,
  ) => {
    filterFieldData['switchEnableSearchCondition'] = true;

    if (type && type === 'date') {
      val = moment(val).format('YYYY-MM-DD');
    }

    if (!filterFieldData[key] || filterFieldData[key] !== val) {
      const oldVal = filterFieldData[key];
      filterFieldData[key] = val;
      if (![undefined, ''].includes(oldVal) && val === '') {
        filterFieldData['callAPI'] = true;
        callAPI();
      } else filterFieldData['callAPI'] = false;
      filterFieldData['isInValidData'] = false;

      if (filterFieldData.fieldtypeoption === 'Relative Filter') {
        setCirLoading(true);
        await GetRelativeFilterItems();
        setCirLoading(false);
      }

      if (
        key !== 'exprInput' &&
        ['Search Condition', 'Aggregate Condition'].includes(filterFieldData.fieldtypeoption)
      ) {
        checkForValidData();
      }

      if (key === 'exprInput' || key === 'greaterThanOrEqualTo' || key === 'lessThanOrEqualTo') {
        if (val === null || val === '' || val === undefined) {
          filterFieldData['switchEnableSearchCondition'] = false;
          filterFieldData.exprTypeTillDate = false;
        } else {
          filterFieldData['switchEnableSearchCondition'] = true;
        }
      }

      if (filterFieldData.exprType === 'between') {
        sliderRange = [filterFieldData.greaterThanOrEqualTo, filterFieldData.lessThanOrEqualTo];
        filterFieldData['callAPI'] = true;
        callAPI();
      } else if (!filterFieldData.exprInput?.length) {
        filterFieldData['switchEnableSearchCondition'] = false;
        filterFieldData.exprTypeTillDate = false;
      }
      dispatch(SetModifyOptions(propKey, 'filter', filterFieldData.uId));
      if (filterFieldData.fieldtypeoption !== 'Relative Filter') {
        updateUserFilterItem(
          propKey,
          0,
          itemIndex,
          constructChartAxesFieldObject(),
          currentChartAxesName,
        );
      }
      callAPI();
    }
  };

  return (
    <div
      ref={(node: any) => drag(drop(node))}
      className="UserFilterCard"
      style={
        filterFieldData.isInValidData
          ? { border: '2px red solid', backgroundColor: 'lightpink' }
          : {}
      }
    >
      {loading ? <LoadingPopover /> : null}

      <FilterField
        field={field}
        filterFieldData={filterFieldData}
        setCirLoading={setCirLoading}
        setLoading={setLoading}
        propKey={propKey}
        token={token}
        uID={uID}
        itemIndex={itemIndex}
        bIndex={bIndex}
      />

      {!filterFieldData.isCollapsed ? (
        <>
          <div
            className="UserSelectionDiv"
            style={
              filterFieldData.isInValidData
                ? { border: '2px red solid', backgroundColor: 'lightpink' }
                : {}
            }
          >
            {filterFieldData.dataType === 'timestamp' || filterFieldData.dataType === 'date' ? (
              <div
                className="CustomRequiredField"
                style={{
                  backgroundColor: 'white',
                  display: 'flex',
                  flexDirection: 'column',
                  rowGap: '8px',
                  paddingTop: '2px',
                }}
              >
                {filterFieldData.fieldtypeoption === 'Pick List' ? (
                  <DropDownForDatePattern
                    items={[
                      ...datePatternCollections,
                      ...(filterFieldData.dataType === 'timestamp'
                        ? [{ key: 'hours', value: 'Hours' }]
                        : []),
                    ]}
                    handleDropDownForDatePatternOnChange={handleDropDownForDatePatternOnChange}
                    value={filterFieldData['prefix']}
                  ></DropDownForDatePattern>
                ) : filterFieldData.fieldtypeoption === 'Search Condition' ? (
                  <DropDownForDatePattern
                    items={[
                      ...datePatternSearchConditionCollections,
                      ...(filterFieldData.dataType === 'timestamp'
                        ? [{ key: 'hours', value: 'Hours' }]
                        : []),
                    ]}
                    handleDropDownForDatePatternOnChange={handleDropDownForDatePatternOnChange}
                    value={filterFieldData['prefix']}
                  ></DropDownForDatePattern>
                ) : null}
              </div>
            ) : null}

            {!filterFieldData.isParameterized && filterFieldData.fieldtypeoption === 'Pick List' ? (
              <>
                <SelecPickListCard
                  handleCBChange={handleCBChange}
                  allOptions={filterFieldData?.rawselectmembers}
                  userSelection={filterFieldData?.userSelection}
                  shouldExclude={filterFieldData?.includeexclude === 'Exclude'}
                  loading={loading}
                ></SelecPickListCard>
                {filterFieldData.dataType === 'timestamp' || filterFieldData.dataType === 'date' ? (
                  <SelecTillDate
                    isDisabled={false}
                    shouldExclude={filterFieldData?.includeexclude === 'Exclude'}
                    timeGrain={filterFieldData.prefix}
                    isChecked={filterFieldData.exprTypeTillDate}
                    handleChangeTillDate={handleChangeTillDate}
                  ></SelecTillDate>
                ) : null}
              </>
            ) : !filterFieldData.isParameterized &&
              filterFieldData.fieldtypeoption === 'Relative Filter' ? (
              <SelecRelativeFilterCard
                filterFieldData={filterFieldData}
                handleCustomRequiredValueOnBlur={handleCustomRequiredValueOnBlur}
                handleDropDownForPatternOnChange={handleDropDownForPatternOnChange}
                cirLoading={cirloading}
                fromDate={filterFieldData.fromDate}
                toDate={filterFieldData.toDate}
                anchorDate={filterFieldData.expTypeAnchorDate}
                specificDate={filterFieldData.exprInputSpecificDate}
                relativeConditionFromType={filterFieldData.expTypeFromRelativeDate}
                relativeConditionToType={filterFieldData.expTypeToRelativeDate}
              ></SelecRelativeFilterCard>
            ) : (
              <>
                {filterFieldData.fieldtypeoption === 'Search Condition' && (
                  <CustomCard
                    filterFieldData={filterFieldData}
                    handleCustomRequiredValueOnBlur={handleCustomRequiredValueOnBlur}
                    handleDropDownForPatternOnChange={handleDropDownForPatternOnChange}
                    dataType={filterFieldData.dataType}
                    operator={filterFieldData.exprType}
                    shouldExclude={filterFieldData?.includeexclude === 'Exclude'}
                    timeGrain={filterFieldData.prefix}
                    value={filterFieldData['exprType']}
                    inputValue={filterFieldData.exprInput}
                    inputValueGreaterThanOrEqualTo={filterFieldData.greaterThanOrEqualTo}
                    inputValueLessThanOrEqualTo={filterFieldData.lessThanOrEqualTo}
                  ></CustomCard>
                )}
                {filterFieldData.fieldtypeoption === 'Top N' && (
                  <TopN
                    filterFieldData={filterFieldData}
                    axisName={currentChartAxesName}
                    propKey={propKey}
                    itemIndex={itemIndex}
                  />
                )}
                {filterFieldData.fieldtypeoption === 'Aggregate Condition' && (
                  <AggregateCondition
                    filterFieldData={filterFieldData}
                    handleCustomRequiredValueOnBlur={handleCustomRequiredValueOnBlur}
                    handleDropDownForPatternOnChange={handleDropDownForPatternOnChange}
                    dataType={filterFieldData.dataType}
                    operator={filterFieldData.exprType}
                    shouldExclude={filterFieldData?.includeexclude === 'Exclude'}
                    timeGrain={filterFieldData.prefix}
                    value={filterFieldData['exprType']}
                    inputValue={filterFieldData.exprInput}
                    inputValueGreaterThanOrEqualTo={filterFieldData.greaterThanOrEqualTo}
                    inputValueLessThanOrEqualTo={filterFieldData.lessThanOrEqualTo}
                  />
                )}
                {/* Dropdown for parameterized */}
                {filterFieldData.isParameterized && (
                  <SelectParameter
                    propKey={propKey}
                    type="chartFilter"
                    filterFieldData={filterFieldData}
                    currentChartAxesName={currentChartAxesName}
                    bIndex={bIndex}
                    itemIndex={itemIndex}
                  />
                )}
              </>
            )}
          </div>
        </>
      ) : null}
    </div>
  );
};

export default UserFilterCard;
